import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Ban } from 'lucide-react';
import { useAvailableTransitions } from '../../hooks/useIssues';
import type { AvailableTransitionItem } from '../../services/issueService';

interface Props {
  projectId: string;
  issueId: string;
  currentStatusName: string;
  currentStatusColor: string;
  onTransition: (statusId: string) => void;
  disabled?: boolean;
}

function getContrastText(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

export default function StatusTransitionDropdown({
  projectId,
  issueId,
  currentStatusName,
  currentStatusColor,
  onTransition,
  disabled = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useAvailableTransitions(projectId, issueId);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const transitions = data?.availableTransitions ?? [];

  // FREE mode: transitionId is null for all items — just show status list
  const isFreeMode = transitions.length > 0 && transitions.every((t) => t.transitionId === null);

  // RESTRICTED mode: separate normal vs global transitions
  const normalTransitions = transitions.filter((t) => t.transitionId !== null);
  const globalTransitions: AvailableTransitionItem[] = []; // global transitions have fromStatusId=null but same shape
  // We'll detect globals by checking if the name doesn't start with currentStatus name
  // Actually, a simpler way: in restricted mode, server returns all matching transitions.
  // We don't have fromStatusId in the response, but global transitions are those with null fromStatusId.
  // Since we don't get that info, we'll show all in one list for restricted mode.
  // If needed later, the server can add isGlobal field.

  const handleSelect = (item: AvailableTransitionItem) => {
    if (!item.isAllowed) return;
    onTransition(item.toStatus.statusId);
    setIsOpen(false);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: currentStatusColor,
          color: getContrastText(currentStatusColor),
        }}
      >
        <span className="w-2 h-2 rounded-full bg-current opacity-60" />
        {currentStatusName}
        {!disabled && <ChevronDown className="size-3.5 opacity-70" />}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="px-3 py-2 text-sm font-medium text-gray-500 border-b border-gray-100">
            상태 변경
          </div>

          {isLoading ? (
            <div className="px-3 py-4 text-center text-sm text-gray-400">로딩 중...</div>
          ) : transitions.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-gray-400">전환 가능한 상태가 없습니다</div>
          ) : isFreeMode ? (
            /* FREE mode: simple status list */
            <div className="py-1 max-h-60 overflow-y-auto">
              {transitions.map((item) => (
                <button
                  key={item.toStatus.statusId}
                  onClick={() => handleSelect(item)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: item.toStatus.color }}
                  />
                  {item.toStatus.name}
                </button>
              ))}
            </div>
          ) : (
            /* RESTRICTED mode: show transition name → target status */
            <div className="py-1 max-h-60 overflow-y-auto">
              {normalTransitions.length > 0 && normalTransitions.map((item) => (
                <TransitionItem key={item.transitionId} item={item} onSelect={handleSelect} />
              ))}
              {globalTransitions.length > 0 && (
                <>
                  <div className="border-t border-gray-100 mx-3 my-1" />
                  <div className="px-3 py-1 text-[11px] text-gray-400">글로벌 전환</div>
                  {globalTransitions.map((item) => (
                    <TransitionItem key={item.transitionId} item={item} onSelect={handleSelect} />
                  ))}
                </>
              )}
              {/* If all transitions are in the normal list (no global detection), just show them */}
              {normalTransitions.length === 0 && transitions.map((item) => (
                <TransitionItem key={`${item.transitionId}-${item.toStatus.statusId}`} item={item} onSelect={handleSelect} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TransitionItem({
  item,
  onSelect,
}: {
  item: AvailableTransitionItem;
  onSelect: (item: AvailableTransitionItem) => void;
}) {
  return (
    <button
      onClick={() => onSelect(item)}
      disabled={!item.isAllowed}
      title={item.disabledReason ?? undefined}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
        item.isAllowed
          ? 'text-gray-700 hover:bg-gray-50 cursor-pointer'
          : 'text-gray-400 cursor-not-allowed opacity-60'
      }`}
    >
      <span className="truncate flex items-center gap-1.5 flex-1">
        {!item.isAllowed && <Ban className="size-3 text-gray-400 shrink-0" />}
        <span className="text-gray-500 truncate">{item.name}</span>
        <span className="text-gray-300 shrink-0">&rarr;</span>
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: item.toStatus.color }}
        />
        <span className="font-medium truncate">{item.toStatus.name}</span>
      </span>
    </button>
  );
}
